import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BatchDetail,
  BatchListItem,
  getBatch,
  listBatches,
  syncBatchChannels,
} from '../../api/batches';
import { Channel, listChannels } from '../../api/channels';
import { useToast } from '../system/ToastContext';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../../styles/tokens';
import { formatInstant } from '../../utils/datetime';
import { InventoryCopyLookupLink } from './InventoryCopyLookupLink';

type SyncChannel = 'shopify' | 'manapool';

const CHANNEL_LABELS: Record<SyncChannel, string> = {
  shopify: 'Shopify',
  manapool: 'Manapool',
};

export const BatchHistoryPanel: React.FC = () => {
  const { showToast } = useToast();
  const [batches, setBatches] = useState<BatchListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BatchDetail | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [selectedChannels, setSelectedChannels] = useState<SyncChannel[]>(['shopify', 'manapool']);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const outboundChannels = useMemo(() => {
    return channels.filter(
      (channel) =>
        (channel.name === 'shopify' || channel.name === 'manapool')
        && channel.sync_enabled
        && channel.outbound_inventory_sync_enabled,
    );
  }, [channels]);

  const loadList = useCallback(async () => {
    setIsLoadingList(true);
    setError(null);
    try {
      const [batchRows, channelResponse] = await Promise.all([
        listBatches({ limit: 50 }),
        listChannels(),
      ]);
      const historical = batchRows.filter((batch) => batch.status !== 'draft');
      setBatches(historical);
      setChannels(channelResponse.items);
      const enabledNames = channelResponse.items
        .filter(
          (channel) =>
            (channel.name === 'shopify' || channel.name === 'manapool')
            && channel.sync_enabled
            && channel.outbound_inventory_sync_enabled,
        )
        .map((channel) => channel.name as SyncChannel);
      if (enabledNames.length > 0) {
        setSelectedChannels(enabledNames);
      }
      if (historical.length > 0 && !selectedId) {
        setSelectedId(historical[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load batch history');
    } finally {
      setIsLoadingList(false);
    }
  }, [selectedId]);

  const loadDetail = useCallback(async (batchId: string) => {
    setIsLoadingDetail(true);
    setError(null);
    try {
      const batch = await getBatch(batchId);
      setDetail(batch);
    } catch (err) {
      setDetail(null);
      setError(err instanceof Error ? err.message : 'Failed to load batch detail');
    } finally {
      setIsLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    void loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const toggleChannel = (name: SyncChannel) => {
    setSelectedChannels((prev) => {
      if (prev.includes(name)) {
        return prev.filter((channel) => channel !== name);
      }
      return [...prev, name];
    });
  };

  const handleSync = async () => {
    if (!selectedId || !detail) return;
    if (selectedChannels.length === 0) {
      showToast('Select at least one channel', 'error');
      return;
    }

    const channelLabel = selectedChannels.map((name) => CHANNEL_LABELS[name]).join(', ');
    const confirmed = window.confirm(
      `Re-sync "${detail.name}" to ${channelLabel}?\n\n`
      + `This emits inventory.updated for variants from this batch (${detail.item_count} line item(s)). `
      + 'History re-sync runs through the dispatcher without waiting on Channel Approvals.',
    );
    if (!confirmed) return;

    setIsSyncing(true);
    setError(null);
    try {
      const result = await syncBatchChannels(selectedId, {
        channels: selectedChannels,
        dry_run: false,
      });
      if (result.variant_count === 0) {
        showToast('No variants found for this batch', 'error');
        return;
      }
      const targets = result.channels.map((name) => CHANNEL_LABELS[name as SyncChannel] || name).join(', ');
      showToast(
        `Queued ${result.variant_count} variant(s) → ${targets}. History re-sync bypasses Approvals; dispatcher should run shortly.`,
        'success',
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sync batch channels';
      setError(message);
      showToast(message, 'error');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <div style={styles.layout}>
      <section style={styles.listPane}>
        <div style={styles.paneHeader}>
          <h2 style={styles.paneTitle}>Submitted batches</h2>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => void loadList()}
            disabled={isLoadingList}
          >
            {isLoadingList ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {error && <div style={styles.errorBanner}>{error}</div>}

        {batches.length === 0 && !isLoadingList ? (
          <p style={styles.emptyText}>No submitted batches yet.</p>
        ) : (
          <ul style={styles.batchList}>
            {batches.map((batch) => {
              const active = batch.id === selectedId;
              return (
                <li key={batch.id}>
                  <button
                    type="button"
                    style={{
                      ...styles.batchRow,
                      ...(active ? styles.batchRowActive : {}),
                    }}
                    onClick={() => setSelectedId(batch.id)}
                  >
                    <span style={styles.batchName}>{batch.name}</span>
                    <span style={styles.batchMeta}>
                      {batch.item_count} item{batch.item_count === 1 ? '' : 's'}
                      {' · '}
                      {formatInstant(batch.submitted_at || batch.created_at)}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section style={styles.detailPane}>
        {!selectedId ? (
          <p style={styles.emptyText}>Select a batch to review items and re-sync channels.</p>
        ) : isLoadingDetail ? (
          <p style={styles.emptyText}>Loading batch…</p>
        ) : !detail ? (
          <p style={styles.emptyText}>Batch detail unavailable.</p>
        ) : (
          <>
            <div style={styles.paneHeader}>
              <div>
                <h2 style={styles.paneTitle}>{detail.name}</h2>
                <p style={styles.detailSubtitle}>
                  Submitted {formatInstant(detail.submitted_at || detail.created_at)}
                  {' · '}
                  {detail.item_count} item{detail.item_count === 1 ? '' : 's'}
                  {' · '}
                  Status {detail.status}
                </p>
              </div>
            </div>

            <p style={styles.syncHint}>
              Re-sync updates Shopify/Manapool for stock already in SyncBase.
              New submits must be approved on Channels → Approvals first;
              pending_approval copies are not sellable and will not list.
            </p>
            <div style={styles.syncBox}>
              <div style={styles.channelRow}>
                {(['shopify', 'manapool'] as SyncChannel[]).map((name) => {
                  const enabled = outboundChannels.some((channel) => channel.name === name);
                  return (
                    <label
                      key={name}
                      style={{
                        ...styles.channelLabel,
                        ...(enabled ? {} : styles.channelLabelDisabled),
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedChannels.includes(name)}
                        disabled={!enabled || isSyncing}
                        onChange={() => toggleChannel(name)}
                      />
                      {CHANNEL_LABELS[name]}
                      {!enabled ? ' (disabled)' : ''}
                    </label>
                  );
                })}
              </div>
              <button
                type="button"
                style={styles.primaryButton}
                onClick={() => void handleSync()}
                disabled={isSyncing || selectedChannels.length === 0 || outboundChannels.length === 0}
              >
                {isSyncing ? 'Queuing…' : 'Sync to selected channels'}
              </button>
            </div>

            <div style={styles.itemTableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Card</th>
                    <th style={styles.th}>Set</th>
                    <th style={styles.th}>Cond</th>
                    <th style={styles.th}>Finish</th>
                    <th style={styles.th}>Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.items || []).map((item) => (
                    <tr key={item.id}>
                      <td style={styles.td}>
                        {item.card_name}
                        <InventoryCopyLookupLink
                          productLine={item.product_line}
                          productName={item.card_name}
                          setName={item.set_name}
                          cardNumber={item.card_number}
                          condition={item.condition}
                          printing={item.printing}
                          language={item.language}
                        />
                      </td>
                      <td style={styles.td}>{item.set_name || '—'}</td>
                      <td style={styles.td}>{item.condition}</td>
                      <td style={styles.td}>
                        {item.printing === 'etched'
                          ? 'Etched'
                          : item.is_foil
                            ? 'Foil'
                            : 'Normal'}
                      </td>
                      <td style={styles.td}>{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(detail.items || []).length === 0 && (
                <p style={styles.emptyText}>No line items on this batch.</p>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  layout: {
    display: 'grid',
    gridTemplateColumns: 'minmax(240px, 320px) 1fr',
    gap: spacing.lg,
    alignItems: 'start',
  },
  listPane: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    minHeight: 320,
  },
  detailPane: {
    backgroundColor: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    padding: spacing.lg,
    minHeight: 320,
  },
  paneHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  paneTitle: {
    margin: 0,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  detailSubtitle: {
    margin: `${spacing.xs} 0 0`,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.textSecondary,
  },
  syncHint: {
    margin: `0 0 ${spacing.sm} 0`,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.textSecondary,
    lineHeight: 1.4,
  },
  batchList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  batchRow: {
    width: '100%',
    textAlign: 'left' as const,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.gray50,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 2,
  },
  batchRowActive: {
    borderColor: colors.primary,
    backgroundColor: colors.white,
    boxShadow: `inset 0 0 0 1px ${colors.primary}`,
  },
  batchName: {
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  batchMeta: {
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
  },
  syncBox: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    marginBottom: spacing.lg,
    padding: spacing.md,
    backgroundColor: colors.gray50,
    borderRadius: borderRadius.sm,
  },
  channelRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: spacing.md,
  },
  channelLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xs,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.text,
    cursor: 'pointer',
  },
  channelLabelDisabled: {
    color: colors.textSecondary,
    cursor: 'not-allowed',
  },
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typographyFlat.fontSize.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
    cursor: 'pointer',
  },
  secondaryButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.white,
    color: colors.text,
    padding: `${spacing.xs} ${spacing.md}`,
    fontSize: typographyFlat.fontSize.sm,
    cursor: 'pointer',
  },
  itemTableWrap: {
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    textAlign: 'left' as const,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    fontSize: typographyFlat.fontSize.xs,
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  td: {
    padding: `${spacing.sm} ${spacing.sm}`,
    borderBottom: `1px solid ${colors.border}`,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.text,
  },
  emptyText: {
    margin: 0,
    fontSize: typographyFlat.fontSize.sm,
    color: colors.textSecondary,
  },
  errorBanner: {
    marginBottom: spacing.md,
    padding: spacing.sm,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.errorLight || '#fde8e8',
    color: colors.error,
    fontSize: typographyFlat.fontSize.sm,
  },
};
